import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {ReactNode} from 'react';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {Dictionary} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {randomString} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {buildCalendarEventVersionUrl} from '../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {
  CalendarEventModel,
  CalendarEventVisualStatusesEnum,
  CompleteCalendarEventModel,
} from '../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionVisualStatusesEnum,
  DraftCalendarEventVersionModel,
} from '../../models/api/calendarEventVersionModel';
import {CalendarVersionModel} from '../../models/api/calendarVersionModel';
import {RecurrenceModel, RecurrenceRuleModel} from '../../models/api/recurrenceModel';

export function isRecurringMasterEvent(calendarEvent: CalendarEventModel) {
  return Boolean(calendarEvent.recurrence && calendarEvent.recurrence.length > 0);
}

export function isRecurringExceptionEvent(calendarEvent: CalendarEventModel) {
  return Boolean(calendarEvent.recurringCalendarEventId);
}

export function isRecurringSaveTypeRequired(calendarEvent: CalendarEventModel) {
  return isRecurringMasterEvent(calendarEvent);
}

type CalendarEventGroup = [CalendarEventModel, CalendarEventVersionModel];

// Given a master event, build a new "exception" instance for a specific time.
export function buildExceptionEventFromMaster(
  calendarVersion: CalendarVersionModel,
  masterCalendarEvent: CompleteCalendarEventModel,
  masterCalendarEventVersion: CalendarEventVersionModel,
  newCalendarEvent: CalendarEventModel,
  newCalendarEventVersion: CalendarEventVersionModel,
  instanceStartDate: number,
  company: CompanyModel,
): CalendarEventGroup {
  const calendarEventUid = randomString();
  const calendarEventVersionUid = randomString();

  // If the master is an all-day event, we have to transform the instanceStartDate to midnight in UTC.
  const originalStartDate = masterCalendarEventVersion.isAllDay
    ? DateTime.fromMillis(instanceStartDate).setZone('utc', {keepLocalTime: true}).valueOf()
    : instanceStartDate;

  const {attendeesCanInviteOthers, attendeesCanModify, shouldIgnoreAttendeeResponse, participants, status} =
    newCalendarEvent;
  const exceptionCalendarEvent: CalendarEventModel = {
    status,
    participants,
    attendeesCanInviteOthers,
    attendeesCanModify,
    shouldIgnoreAttendeeResponse,
    uid: calendarEventUid,
    recurrence: undefined,
    recurringCalendarEventId: masterCalendarEvent.id,
    originalStartDate,
    calendarEventVersionUids: [calendarEventVersionUid],
    visualStatus: CalendarEventVisualStatusesEnum.DRAFT,
    updatedAt: 0,
  };

  const exceptionCalendarEventVersion: DraftCalendarEventVersionModel = {
    ...newCalendarEventVersion,
    uid: calendarEventVersionUid,
    channelId: undefined,
    recurrence: undefined,
    calendarEventVersionUrl: buildCalendarEventVersionUrl(company, calendarEventVersionUid),
    calendarEventUid,
    calendarVersionIds: [calendarVersion.id],
    visualStatus: CalendarEventVersionVisualStatusesEnum.DRAFT,
  };

  return [exceptionCalendarEvent, exceptionCalendarEventVersion];
}

export function retrieveMasterEventForId(
  masterCalendarEventIdToUidMap: Dictionary<string>,
  calendarEvents: ResourceMap<CalendarEventModel>,
  calendarEventVersions: ResourceMap<CalendarEventVersionModel>,
  masterCalendarEventId: number,
  calendarVersionId: number,
): CalendarEventGroup {
  const masterEventUid = masterCalendarEventIdToUidMap[masterCalendarEventId];
  const masterCalendarEvent = masterEventUid && calendarEvents[masterEventUid];
  if (!masterCalendarEvent) {
    throw new Error(`A master calendarEvent could not be found for uid ${masterEventUid}`);
  }

  const masterCalendarEventVersions = _(masterCalendarEvent.calendarEventVersionUids)
    .map((uid) => calendarEventVersions[uid])
    .compact()
    .value();

  const matchingCalendarEventVersion = masterCalendarEventVersions.find((v) =>
    v.calendarVersionIds.includes(calendarVersionId),
  );
  if (!matchingCalendarEventVersion) {
    throw new Error(
      `A master calendarEventVersion could not be found for calendarVersion ID ${calendarVersionId}`,
    );
  }

  return [masterCalendarEvent, matchingCalendarEventVersion];
}

// If a user makes edits to an instance of a recurring series and then chooses
// to apply those changes to all event in the series, we have to update the master event's properties.
export function applyInstanceChangesToMaster(
  masterCalendarEvent: CalendarEventModel,
  masterCalendarEventVersion: CalendarEventVersionModel,
  instanceCalendarEvent: CalendarEventModel,
  instanceCalendarEventVersion: CalendarEventVersionModel,
): CalendarEventGroup {
  const newMasterCalendarEvent: CalendarEventModel = {
    ...masterCalendarEvent,
    participants: instanceCalendarEvent.participants,
  };

  const newMasterCalendarEventVersion: CalendarEventVersionModel = {
    ...masterCalendarEventVersion,
    summary: instanceCalendarEventVersion.summary,
    description: instanceCalendarEventVersion.description,
    locations: instanceCalendarEventVersion.locations,
    color: instanceCalendarEventVersion.color,
    showAs: instanceCalendarEventVersion.showAs,
    visibility: instanceCalendarEventVersion.visibility,
    reminders: instanceCalendarEventVersion.reminders,
    isRemoved: instanceCalendarEventVersion.isRemoved,
  };

  return [newMasterCalendarEvent, newMasterCalendarEventVersion];
}

// freq is omitted since it does not have a logical default value
export const baseRecurrenceRule: Omit<RecurrenceRuleModel, 'freq'> = {
  dtstart: undefined,
  interval: 1,
  wkst: undefined,
  count: undefined,
  until: undefined,
  tzid: undefined,
  bysetpos: undefined,
  bymonth: undefined,
  bymonthday: undefined,
  byyearday: undefined,
  byweekno: undefined,
  byweekday: undefined,
  byhour: undefined,
  byminute: undefined,
  bysecond: undefined,
};

// Given a partial RecurrenceRule, constructs and returns a complete RecurrenceModel
export function buildRecurrence(
  recurrenceRuleOverrides: Pick<RecurrenceRuleModel, 'freq'> & Partial<RecurrenceRuleModel>,
): RecurrenceModel {
  return {
    rrules: [
      {
        ...baseRecurrenceRule,
        ...recurrenceRuleOverrides,
      },
    ],
    rdates: [],
    exdates: [],
  };
}

export const RecurrenceWeekOfMonthToText: {[n: string]: ReactNode} = {
  1: <Trans id="recurrence-first-week">first</Trans>,
  2: <Trans id="recurrence-second-week">second</Trans>,
  3: <Trans id="recurrence-third-week">third</Trans>,
  4: <Trans id="recurrence-fourth-week">fourth</Trans>,
  5: <Trans id="recurrence-fifth-week">fifth</Trans>,
  '-1': <Trans id="recurrence-last-week">last</Trans>,
} as const;

const daysPerWeek = 7;

// Returns an integer representing the nth occurrence of the
// provided weekday within the month that it is in. If it's
// the last occurrence of that weekday, returns -1.
// See https://tools.ietf.org/html/rfc5545#page-41
export function getWeekOfMonth(dateTime: DateTime<true>) {
  const {day, daysInMonth} = dateTime;

  const weekOfMonth = Math.ceil(day / daysPerWeek);
  const isLastOccurrenceOfDayInMonth = daysInMonth - day < daysPerWeek;

  if (isLastOccurrenceOfDayInMonth) {
    return -1;
  }

  return weekOfMonth;
}
